import axios, { AxiosInstance } from 'axios';
import qs from 'qs';
import { LogoutParams } from '../@types/authentication';
import releaseConfig from '../releaseConfig';
import { JsonPatchFunction } from './jsonPatch';
import { getAiApiUrl, getAmApiUrl, getApiUrl, getDwApiUrl } from './urls/getUrls';
import { refreshAccessToken, clearTokens } from './oidcTokenService';
import { setSession } from './jwt';

export type { AxiosRequestConfig } from 'axios';

export interface ExtendedAxiosInstance extends AxiosInstance {
  jsonPatch: JsonPatchFunction;
}

//callback method to be called when the user is logged out, this is used to redirect the user to the login page
//the value is set in the App.tsx file
let logoutCallback: ((params?: LogoutParams) => Promise<void>) | null = null;
export const setLogoutCallback = (logoutFn: () => Promise<void>) => {
  logoutCallback = logoutFn;
};

const axiosInstance: ExtendedAxiosInstance = axios.create({
  baseURL: getApiUrl()
}) as ExtendedAxiosInstance;

export const axiosAmApiInstance = axios.create({
  baseURL: getAmApiUrl()
});

export const axiosEtrack1Instance = axios.create({
  baseURL: releaseConfig.etrack1Url.startsWith('#{')
    ? 'https://etrack1dev.azurewebsites.net' //'https://etrack1.nps.today'
    : releaseConfig.etrack1Url
});

export const axiosHtmlFilesInstance = axios.create({
  baseURL: releaseConfig.htmlFilesUrl.startsWith('#{')
    ? import.meta.env.VITE_HTML_FILES_URL ||
      'https://npstodaydevelopment.blob.core.windows.net/html-files'
    : releaseConfig.htmlFilesUrl
});

export const axiosAiInstance: ExtendedAxiosInstance = axios.create({
  baseURL: getAiApiUrl(),
  paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' })
}) as ExtendedAxiosInstance;

export const axiosDwInstance: ExtendedAxiosInstance = axios.create({
  baseURL: getDwApiUrl(),
  allowAbsoluteUrls: false,
  paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' })
}) as ExtendedAxiosInstance;

axiosDwInstance.interceptors.request.use((config) => {
  if (!config.baseURL) {
    throw new Error('DW API URL is not configured');
  }
  return config;
});

// Add a request interceptor to include the timezone header
axiosInstance.interceptors.request.use(
  (config) => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    config.headers['User-Timezone'] = timezone;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

let isRefreshing = false;
let failedQueue: { resolve: (value?: unknown) => void; reject: (reason?: any) => void }[] = [];

const TOKEN_ENDPOINT = '/connect/token';
const TENANT_CHANGE_GRANT_TYPE = 'tenant_change';

const getGrantType = (data: unknown): string | undefined => {
  if (!data) {
    return undefined;
  }

  if (typeof data === 'string') {
    const parsedData = qs.parse(data);
    return typeof parsedData.grant_type === 'string' ? parsedData.grant_type : undefined;
  }

  if (data instanceof URLSearchParams) {
    return data.get('grant_type') ?? undefined;
  }

  if (typeof data === 'object' && 'grant_type' in data) {
    const grantType = (data as { grant_type?: unknown }).grant_type;
    return typeof grantType === 'string' ? grantType : undefined;
  }

  return undefined;
};

const shouldRefreshAccessTokenForRequest = (originalRequest: any) => {
  const isTokenEndpoint = originalRequest?.url?.includes(TOKEN_ENDPOINT);
  if (!isTokenEndpoint) {
    return true;
  }

  return getGrantType(originalRequest.data) === TENANT_CHANGE_GRANT_TYPE;
};

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

/**
 * Attaches a response interceptor that handles 401 errors by refreshing the access token
 * and retrying the original request. Can be applied to any axios instance.
 */
const attachAuthInterceptor = (instance: AxiosInstance) => {
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      const token = localStorage.getItem('accessToken');
      const isAuthenticated = !!token;
      const isUnauthorized = isAuthenticated && error.response?.status === 401;

      if (
        originalRequest &&
        isUnauthorized &&
        !originalRequest._retry &&
        shouldRefreshAccessTokenForRequest(originalRequest)
      ) {
        if (isRefreshing) {
          // Queue this request until the refresh completes
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((newToken) => {
              originalRequest.headers = originalRequest.headers ?? {};
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return instance(originalRequest);
            })
            .catch((err) => {
              throw err;
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const tokenResponse = await refreshAccessToken();

          if (tokenResponse) {
            setSession(tokenResponse.access_token);
            processQueue(null, tokenResponse.access_token);
            originalRequest.headers = originalRequest.headers ?? {};
            originalRequest.headers.Authorization = `Bearer ${tokenResponse.access_token}`;
            return await instance(originalRequest);
          } else {
            // No refresh token available - logout
            processQueue(error, null);
            clearTokens();
            logoutCallback?.({
              logoutInfoMessage: {
                title: 'Session Expired',
                description: 'Your session has expired. Please log in again to continue.'
              }
            });
          }
        } catch (refreshError) {
          processQueue(refreshError, null);
          clearTokens();
          logoutCallback?.({
            logoutInfoMessage: {
              title: 'Session Expired',
              description: 'Your session has expired. Please log in again to continue.'
            }
          });
          throw refreshError;
        } finally {
          isRefreshing = false;
        }
      }

      throw error.response?.data || error.message || 'Something went wrong';
    }
  );
};

attachAuthInterceptor(axiosInstance);

export default axiosInstance;

attachAuthInterceptor(axiosAiInstance);
attachAuthInterceptor(axiosDwInstance);
attachAuthInterceptor(axiosAmApiInstance);
